import React, { useEffect, useRef, useState } from "react";
import { Volume2, Square, Star } from "lucide-react";
import { useReviewLink } from "../lib/notice";
import { expOn } from "../lib/experience";

const LINES = {
  bridal: "This piece will carry your family's story forward. Congratulations.",
  gold: "You have made a wise and meaningful choice. Regal is honored.",
  custom: "Your vision is now in our hands. We will not let you down.",
  default: "A beautiful choice. Jewelry chosen with the heart is never just gold — it becomes part of the family. We will take it from here.",
};
const AUDIO = {
  bridal: "/audio/closing-bridal.mp3",
  gold: "/audio/closing-gold.mp3",
  custom: "/audio/closing-custom.mp3",
  default: "/audio/line-closing.mp3",
};
const REVIEW_COPY =
  "Before you go — Regal Jewels is a family business built on trust, knowledge and the relationships we form with every guest. When you share your experience on Google, you help other families find us and help us grow into something even more special. It takes just a moment and means the world to us.";
const REVIEW_FALLBACK = "https://g.page/r/CZzcCtQ0iBhXEAE/review";

// Mr. Regal's closing line after an inquiry/booking + a warm one-time Google review ask
export function RegalClosing({ context = "default", className = "" }) {
  const link = useReviewLink() || REVIEW_FALLBACK;
  const [playing, setPlaying] = useState(false);
  const [showReview, setShowReview] = useState(() => localStorage.getItem("rj_review_asked") !== "1");
  const audioRef = useRef(null);
  const line = LINES[context] || LINES.default;

  const dismissReview = () => {
    localStorage.setItem("rj_review_asked", "1");
    setShowReview(false);
  };

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.pause();
  }, []);

  const toggle = () => {
    if (playing) {
      if (audioRef.current) audioRef.current.pause();
      setPlaying(false);
      return;
    }
    try {
      const a = new Audio(AUDIO[context] || AUDIO.default);
      audioRef.current = a;
      a.volume = 0.6;
      a.onended = () => setPlaying(false);
      a.play().then(() => setPlaying(true)).catch(() => {});
    } catch (e) {
      console.debug("closing audio failed:", e?.message);
    }
  };

  return (
    <div className={className} data-testid="regal-closing">
      <div className="flex items-start justify-center gap-4 text-left max-w-lg mx-auto">
        <img src="/mr-regal-welcome.webp" alt="" aria-hidden="true" loading="lazy" className="h-24 w-auto shrink-0" />
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#a8842c]">Mr. Regal</p>
          <p className="font-heading italic text-sm sm:text-base leading-relaxed text-[#3f3a30] mt-1.5" data-testid="regal-closing-line">
            “{line}”
          </p>
          {expOn("voiceNarration") && (
            <button
              onClick={toggle}
              className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-[#8a6a1f] hover:text-[#171310] transition-colors"
              data-testid="regal-closing-play"
            >
              {playing ? <Square size={11} /> : <Volume2 size={12} />} {playing ? "Stop" : "Tap to hear Mr. Regal"}
            </button>
          )}
        </div>
      </div>
      {showReview && expOn("reviewPrompt") && (
        <div className="max-w-lg mx-auto mt-6 border border-[#e0d5bb] bg-[#f4eddc] p-6 text-center" data-testid="review-prompt">
          <Star size={18} className="mx-auto fill-[#c9a24b] text-[#c9a24b]" />
          <p className="text-sm text-[#5c5240] leading-relaxed mt-3">{REVIEW_COPY}</p>
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={dismissReview}
              className="inline-flex items-center gap-2 bg-[#171310] text-[#e9d9ac] hover:bg-[#3a2f1d] text-[11px] tracking-[0.15em] uppercase px-5 py-3 transition-colors"
              data-testid="review-prompt-google"
            >
              <Star size={12} /> Share My Experience
            </a>
            <button
              onClick={dismissReview}
              className="text-[11px] tracking-[0.15em] uppercase text-[#5c5240] underline underline-offset-4 decoration-[#b3a88f] hover:text-[#171310] px-4 py-3 transition-colors"
              data-testid="review-prompt-later"
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
